import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useToastStore } from "@/store/toastStore";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  Phone,
  Email,
} from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import { driverService } from "@/services/driver.service";
import { format } from "date-fns";

type StatusFilter = "all" | "active" | "inactive";

/**
 * Page de gestion des livreurs
 * Basée sur la maquette driver_personnel_management_1
 */
export function DriversPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = usePersistedState("drivers.searchQuery", "");
  const [statusFilter, setStatusFilter] = usePersistedState<StatusFilter>("drivers.statusFilter", "all");

  // État pour le dialog de confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    driverId: string | null;
    driverName: string;
  }>({
    open: false,
    driverId: null,
    driverName: "",
  });

  // Récupérer les livreurs (tous pour gérer le filtre côté client)
  const {
    data: drivers = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => driverService.getAll(true), // Récupérer tous (actifs et inactifs)
  });

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour supprimer un livreur
  const deleteMutation = useMutation({
    mutationFn: (id: string) => driverService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      success("Livreur supprimé avec succès");
      setDeleteConfirm({ open: false, driverId: null, driverName: "" });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la suppression du livreur",
      );
    },
  });

  // Filtrer les livreurs
  const filteredDrivers = drivers.filter((driver) => {
    // Recherche par nom, téléphone ou email
    const matchesSearch =
      !searchQuery ||
      `${driver.firstName} ${driver.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      driver.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.email?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filtre par statut
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && driver.isActive) ||
      (statusFilter === "inactive" && !driver.isActive);

    return matchesSearch && matchesStatus;
  });

  // Trier par nom
  const sortedDrivers = [...filteredDrivers].sort((a, b) => {
    const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
    const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const handleDeleteClick = (
    id: string,
    firstName: string,
    lastName: string,
  ) => {
    setDeleteConfirm({
      open: true,
      driverId: id,
      driverName: `${firstName} ${lastName}`,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.driverId) {
      deleteMutation.mutate(deleteConfirm.driverId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, driverId: null, driverName: "" });
  };

  // Générer les initiales pour l'avatar
  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Formater la date d'embauche
  const formatHireDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Chargement des livreurs..." />;
  }

  if (queryError) {
    return <ErrorMessage message="Erreur lors du chargement des livreurs." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Livreurs"
        subtitle="Gérez la logistique de la flotte et la disponibilité des livreurs"
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/drivers/new")}
          >
            Nouveau Livreur
          </Button>
        }
      />

      <FiltersCard>
        <InputField
            placeholder="Rechercher par nom, téléphone ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            icon={<Search />}
            iconPosition="start"
            sx={{ flexGrow: 1, minWidth: 280, "& .MuiOutlinedInput-root": { height: 40 } }}
          />

          {/* Filtres de statut */}
          <Box sx={{ display: "flex", gap: 0, flexWrap: "wrap" }}>
            <Button
              variant={statusFilter === "all" ? "primary" : "outline"}
              size="small"
              onClick={() => setStatusFilter("all")}
              sx={{ borderRadius: "4px 0 0 4px", minWidth: "auto", px: 2 }}
            >
              Tous
            </Button>
            <Button
              variant={statusFilter === "active" ? "primary" : "outline"}
              size="small"
              onClick={() => setStatusFilter("active")}
              sx={{
                borderRadius: 0,
                minWidth: "auto",
                px: 2,
                ...(statusFilter === "active" && { bgcolor: "success.main", "&:hover": { bgcolor: "success.dark" } }),
              }}
            >
              Disponibles
            </Button>
            <Button
              variant={statusFilter === "inactive" ? "primary" : "outline"}
              size="small"
              onClick={() => setStatusFilter("inactive")}
              sx={{ borderRadius: "0 4px 4px 0", minWidth: "auto", px: 2 }}
            >
              Inactifs
            </Button>
          </Box>
      </FiltersCard>

      {/* Tableau des livreurs */}
      {sortedDrivers.length === 0 ? (
        <EmptyState
          title="Aucun livreur trouvé"
          description={
            searchQuery || statusFilter !== "all"
              ? "Aucun livreur ne correspond aux critères de recherche."
              : "Il n'y a aucun livreur pour le moment. Créez-en un nouveau !"
          }
          action={
            !searchQuery && statusFilter === "all"
              ? {
                  label: "Créer un livreur",
                  onClick: () => navigate("/drivers/new"),
                }
              : undefined
          }
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>Avatar</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Nom du livreur</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Coordonnées</TableCell>
                  <TableCell>Date d&apos;embauche</TableCell>
                  <TableCell>Zones affectées</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="center">Charge</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDrivers.map((driver) => (
                  <TableRow key={driver.id} hover>
                    <TableCell>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        {getInitials(driver.firstName, driver.lastName)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {driver.firstName} {driver.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: #{driver.id.slice(0, 8).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {driver.phone && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Phone
                              sx={{ fontSize: 16, color: "primary.main" }}
                            />
                            <Typography variant="body2">
                              {driver.phone}
                            </Typography>
                          </Box>
                        )}
                        {driver.email && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Email
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {driver.email}
                            </Typography>
                          </Box>
                        )}
                        {!driver.phone && !driver.email && (
                          <Typography variant="body2" color="text.secondary">
                            Aucune coordonnée
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatHireDate(driver.hireDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {driver.zones?.length ? (
                          driver.zones.map((z) => (
                            <Chip
                              key={z.id}
                              label={z.name}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={driver.isActive ? "Disponible" : "Inactif"}
                        color={driver.isActive ? "success" : "default"}
                        size="small"
                        icon={
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: driver.isActive
                                ? "success.main"
                                : "text.disabled",
                            }}
                          />
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      {/* TODO: Ajouter le nombre de livraisons assignées une fois que l'API sera disponible */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        0
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/drivers/${driver.id}/edit`)}
                        aria-label="Modifier"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteClick(
                            driver.id,
                            driver.firstName,
                            driver.lastName,
                          )
                        }
                        aria-label="Supprimer"
                        color="error"
                        disabled={deleteMutation.isPending}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Compteur de résultats */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              {sortedDrivers.length} livreur
              {sortedDrivers.length > 1 ? "s" : ""} trouvé
              {sortedDrivers.length !== drivers.length
                ? ` sur ${drivers.length}`
                : ""}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Supprimer le livreur"
        message={`Êtes-vous sûr de vouloir supprimer le livreur "${deleteConfirm.driverName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
